import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Callout,
  CardActions,
  Cluster,
  CompactRow,
  CompactRowList,
  EmptyState,
  Grid,
  HeaderActions,
  Section,
  Skeleton,
  Split,
  Stack,
  Surface,
} from '../components/ui';
import type { ActionDescriptor } from '../components/ui';
// Story 2-147wa — imported directly from `PageLayouts.tsx` (not the barrel):
// `e2e/scripts/check-route-layout-conformance.mjs`'s own static detection
// (`layoutComponentsUsedBy`) greps a page file's source text for an import
// whose `from` path literally contains `PageLayouts` — a barrel re-export
// (`from '../components/ui'`) is invisible to that regex by design (its own
// header comment: "does the route's real page file import the ONE
// PageLayouts.tsx component"). This is the first real product-page consumer
// (DesignSystemPage's own gallery demo imports via the barrel instead,
// harmless there since `/app/design` carries no registered family — see that
// check's own `if (!family) continue` guard).
import { DashboardLayout } from '../components/ui/PageLayouts';
import { KpiCard } from '../components/dashboard/KpiCard';
import { ActivationChecklistCard } from '../components/dashboard/ActivationChecklistCard';
import { DashboardCustomizeControl } from '../components/dashboard/DashboardCustomizeControl';
import { getHome, homeKeys, type HomeNextWorkItem } from '../lib/api/home.gen';
import { getDashboardLayout, dashboardLayoutKeys, type DashboardWidgetKey } from '../lib/api/dashboardLayout.gen';
import { useAuth } from '../lib/auth';
import { useCapabilityGate } from '../lib/capabilities';
import { errorMessage } from '../lib/errorMessage';
import { formatDistanceToNow } from '../lib/time';
import { useVocab } from '../lib/vocab';

// The launch dashboard (P16 §6.1, WGTM step 1/11; story 2-105).
//
// ═══════════════════════════════════════════════════════════════════════════
// ONE CALL, ONE SCREEN
// ═══════════════════════════════════════════════════════════════════════════
// Everything below is driven by ONE `getHome()` read (`GET /v1/home`,
// `home.service.js`'s own aggregate) — no client-side composition of three
// separate lists, no fabricated stat. `docs/prd/16-build-order.md`'s
// "Release cut" note names this endpoint explicitly and forbids it becoming
// a generic dashboard-BFF; this page is its one and only reader.
//
// Deliberately REMOVED, not softened (this story's own done-when names each
// one): the hardcoded "4.3 / 5 Avg. condition" rating, the fabricated
// "94% accuracy on issue detection" AI-analysis claim, and the
// "Multi-language reports coming soon" / "Voice dictation in beta"
// speculative bullets. None of them were backed by any computation — they
// were literals in the old JSX. Nothing here replaces them with a softer
// version; the sections are gone.
//
// ═══════════════════════════════════════════════════════════════════════════
// STATES
// ═══════════════════════════════════════════════════════════════════════════
// loading  — initial fetch, G-24 skeletons.
// slow     — `isLoading` past `SLOW_MS`: an inline notice, never a second
//            spinner (same idiom `CreateTemplateDialog`'s `SLOW_GENERATION_MS`
//            already established for a wait that is not a failure).
// partial  — does not exist here, on purpose: the aggregate is ONE atomic
//            response. There is no section that can be "loaded" while another
//            is not; inventing a partial state for a single round trip would
//            be UI for a case the API cannot produce.
// error    — `Callout` + retry, server's own message (G-27).
// stale    — `meta.generatedAt` (server-stamped, P05 §8's "cached/stale data
//            is labelled with age" idiom) surfaced honestly once its age
//            passes `STALE_MS`, with a manual refresh — never silently shown
//            as if it were current.
// live     — `refetchOnWindowFocus` + a background poll + a manual Refresh
//            button. No websocket/SSE channel exists for this data (the only
//            SSE stream at HEAD is governance, per `myDay.service.js`'s own
//            note on the same question), so "live" here is react-query
//            refetch, exactly as `MyDayPage` already does it — not a push.
const SLOW_MS = 4000;
const STALE_MS = 5 * 60 * 1000;
const POLL_MS = 60_000;

const workSurfaceFor = (inspectionId: string) => `/app/inspections/${inspectionId}/capture`;

const DUE_LABEL: Record<HomeNextWorkItem['dueState'], string> = {
  overdue: 'Overdue',
  due_today: 'Due today',
  scheduled: 'Scheduled',
  none: 'No due date',
};

const DUE_TONE: Record<HomeNextWorkItem['dueState'], 'destructive' | 'warning' | 'neutral'> = {
  overdue: 'destructive',
  due_today: 'warning',
  scheduled: 'neutral',
  none: 'neutral',
};

// Story 2-143b's own fixed catalog, mirrored here ONLY as the optimistic
// fallback order used before `GET /v1/dashboard-layout` resolves — a fresh,
// uncustomised principal's real server response is this exact order with
// every widget pinned, so rendering it before the round trip completes costs
// nothing for the common case and avoids gating the WHOLE page's first paint
// on a second query the way `isLoading` already gates it on `/v1/home` alone.
const DEFAULT_WIDGET_ORDER: DashboardWidgetKey[] = ['kpiSummary', 'nextWork', 'recentReports', 'activationChecklist'];

// The dashboard renders three effective SLOTS, not four — `nextWork` and
// `recentReports` stay one combined two-column section (2-143a's own "Next
// up" / "Recently approved" pairing), never two independently-positioned
// blocks, so reordering the catalog moves that pair as a unit. `slotOrderOf`
// walks the user's own pinned-widget order and folds whichever of the pair is
// seen FIRST into a single `workSection` slot, preserving its position.
type DashboardSlot = 'kpiSummary' | 'workSection' | 'activationChecklist';
const slotOrderOf = (pinnedKeys: DashboardWidgetKey[]): DashboardSlot[] => {
  const slots: DashboardSlot[] = [];
  pinnedKeys.forEach((key) => {
    if (key === 'kpiSummary' || key === 'activationChecklist') {
      slots.push(key);
    } else if (!slots.includes('workSection')) {
      slots.push('workSection');
    }
  });
  return slots;
};

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Vocabulary layer (P03 §5, story 3-2): every property/inspection is
  // building-domain today, so the unqualified default-domain noun is exact.
  const { vocab } = useVocab();
  const subjectNounPlural = vocab('Subjects');
  const subjectLower = vocab('Subject').toLowerCase();

  // G-13: every "New X" CTA reads its gate, never a bare permission guess.
  const newSiteGate = useCapabilityGate(undefined, 'subjects:manage');
  const newInspectionGate = useCapabilityGate('scheduling.core', 'inspections:create');

  // G-18 warns any plain `useQuery` to consider `useCursorList()` — that gate
  // is for a PAGED list. `GET /v1/home` answers one atomic object, not a
  // cursor envelope, so there is no list to page; a plain `useQuery` is the
  // correct shape here, same reasoning `DashboardPage`'s own prior properties-
  // total query documented for the same warning.
  const { data, isLoading, isFetching, isError, error, refetch, fetchStatus } = useQuery({
    queryKey: homeKeys.list({ org: user?.organizationId }),
    queryFn: () => getHome(),
    enabled: Boolean(user),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: POLL_MS,
  });

  // Story 2-143b — dashboard personalization's own layout metadata (LAYOUT
  // ONLY: which of the fixed widget keys is pinned, in what order — no
  // content, so this never competes with `/v1/home` for the "one call, one
  // screen" rule the header above documents). A second, independent query:
  // `GET /v1/dashboard-layout` answers "what does this user want to see",
  // orthogonal to "what IS there to see" (`/v1/home`) — the page renders
  // correctly from either resolving first, via `DEFAULT_WIDGET_ORDER`'s
  // optimistic fallback above.
  const dashboardLayoutQueryKey = dashboardLayoutKeys.list({ org: user?.organizationId });
  const { data: layoutData } = useQuery({
    queryKey: dashboardLayoutQueryKey,
    queryFn: () => getDashboardLayout(),
    enabled: Boolean(user),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!isLoading) return undefined;
    const timer = window.setTimeout(() => setSlow(true), SLOW_MS);
    return () => window.clearTimeout(timer);
  }, [isLoading]);

  const offline = fetchStatus === 'paused';
  const generatedAgeMs = data ? Date.now() - new Date(data.meta.generatedAt).getTime() : 0;
  const isStale = Boolean(data) && generatedAgeMs > STALE_MS;
  const freshnessLabel = data ? `Updated ${formatDistanceToNow(data.meta.generatedAt)}` : null;

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="Your dashboard could not be loaded"
        headingLevel={2}
        actions={
          <CardActions
            align="start"
            actions={[{ id: 'retry', label: 'Try again', variant: 'secondary', onClick: () => refetch() }]}
          />
        }
        data-testid="dashboard-error"
      >
        {/* G-27 — the server's own sentence, via the shared helper. */}
        {errorMessage(error)}
      </Callout>
    );
  }

  if (isLoading || !data) {
    return (
      <Stack gap={6} data-testid="dashboard-loading">
        <Grid columns={{ base: 1, md: 4 }} gap={4}>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </Grid>
        <Skeleton className="h-48 w-full" />
        {slow && (
          <Callout tone="info" title="Still loading" data-testid="dashboard-slow">
            This is taking longer than usual. You can keep waiting, or try again.
            <div className="mt-2">
              <CardActions
                align="start"
                actions={[{ id: 'retry', label: 'Try again', variant: 'secondary', onClick: () => refetch() }]}
              />
            </div>
          </Callout>
        )}
      </Stack>
    );
  }

  const { counts, nextWork, recentApprovedReports, zeroState } = data;
  const activeInspections = counts.inspections.in_progress + counts.inspections.assigned + counts.inspections.scheduled;
  const inReview = counts.inspections.submitted + counts.inspections.in_review;

  // Story 2-143b — which widgets this principal has PINNED, in their own
  // order: `layoutData` may not have resolved yet (a second, independent
  // query from `/v1/home`), so `DEFAULT_WIDGET_ORDER` stands in until it
  // does — the real server default for an uncustomised principal anyway, so
  // this never visibly flashes for the common case. A widget the server
  // marks `allowed:false` is NEVER reported `pinned:true` regardless of what
  // was last saved (`dashboardLayout.service.js`'s own header) — the
  // permission clamp that keeps personalization from becoming a scope
  // bypass, already applied before this page ever sees the response.
  const pinnedKeys: DashboardWidgetKey[] = layoutData
    ? layoutData.widgets.filter((w) => w.pinned).map((w) => w.key)
    : DEFAULT_WIDGET_ORDER;
  const pinnedSet = new Set(pinnedKeys);
  const slotOrder = slotOrderOf(pinnedKeys);

  // Story 2-143a: "empty regions collapse instead of occupying fixed card
  // space." A region is empty when ITS OWN list has nothing in it — not when
  // the whole org is unset up (that is `zeroState.isEmpty`, handled below,
  // already a full-page branch). When only one of "Next up"/"Recently
  // approved" is empty, that one panel shrinks to a minimal single-line
  // affordance instead of a full card with placeholder text inside; when
  // BOTH are empty the whole two-panel section is omitted rather than
  // showing two rows that both say "nothing here". Story 2-143b adds a
  // SECOND way a side can be absent — the user (or their role) has it
  // unpinned — and that case shows NEITHER the full card NOR the collapsed
  // one-liner: an unpinned widget is not "empty", it is simply not on this
  // dashboard, by choice.
  const nextWorkPinned = pinnedSet.has('nextWork');
  const recentReportsPinned = pinnedSet.has('recentReports');
  const hasNextWork = nextWorkPinned && nextWork.length > 0;
  const hasApprovedReports = recentReportsPinned && recentApprovedReports.length > 0;
  const showWorkSection = nextWorkPinned || recentReportsPinned;

  // Story 2-130 (UI-DS-00 §3.1/§4.2; 2026-08-22 spec §4.2): `PageHeader`
  // replaces the hand-rolled `sr-only` h1 + freshness/refresh row + separate
  // "Quick actions" glass-card with the shell's real title/freshness/quick-
  // action primitive. `title`/`freshnessLabel`/`refetch`/gates are the exact
  // same state this page already computed above — PageHeader only changes
  // HOW they're composed, not what they are. The h1 is now genuinely visible
  // (previously `sr-only`) — same accessible name ("Dashboard"), no change to
  // any testid/role/logic story-2-105/2-106 assert against.
  const freshness = freshnessLabel && (
    <Cluster gap={2} data-testid="dashboard-freshness-row">
      <span
        data-testid="dashboard-freshness"
        data-stale={isStale ? 'true' : 'false'}
        data-offline={offline ? 'true' : 'false'}
      >
        {offline ? `Offline · showing a cached copy · ${freshnessLabel.toLowerCase()}` : freshnessLabel}
      </span>
      <CardActions
        align="start"
        actions={[
          {
            id: 'refresh',
            label: 'Refresh',
            variant: 'ghost',
            iconOnly: true,
            loading: isFetching,
            icon: <ArrowPathIcon className="h-4 w-4" />,
            onClick: () => refetch(),
            'data-testid': 'dashboard-refresh',
          },
        ]}
      />
    </Cluster>
  );

  // Story 2-147wa — the quick-action cluster is now an `ActionDescriptor[]`
  // fed to `HeaderActions` (P10 §2.7 row 2) instead of three hand-rolled
  // `<Button>`s: the shell decides control size/variant/overflow, this page
  // only states WHAT each action is. `DashboardCustomizeControl` stays a
  // sibling (not an `ActionDescriptor` — it is a whole popover with its own
  // internal state, not a single command `HeaderActions`' closed vocabulary
  // can express; see `ActionHosts.tsx`'s own "NO REACTNODE ESCAPE HATCH"
  // header comment for why a host cannot accept it directly instead).
  const quickActionDescriptors: ActionDescriptor[] = [
    ...(newSiteGate.allowed
      ? [
          {
            id: 'new-site',
            label: `New ${subjectLower}`,
            icon: <BuildingOffice2Icon className="h-4 w-4" />,
            onClick: () => navigate('/app/sites'),
            'data-testid': 'dashboard-new-site',
          },
        ]
      : []),
    ...(newInspectionGate.allowed
      ? [
          {
            id: 'new-inspection',
            label: 'New inspection',
            variant: 'primary' as const,
            icon: <PlusIcon className="h-4 w-4" />,
            onClick: () => navigate('/app/schedule/new'),
            'data-testid': 'dashboard-new-inspection',
          },
        ]
      : []),
    {
      id: 'view-reports',
      label: 'View reports',
      icon: <DocumentTextIcon className="h-4 w-4" />,
      onClick: () => navigate('/app/reports'),
      'data-testid': 'dashboard-view-reports',
    },
  ];

  const quickActions = (
    <>
      <HeaderActions actions={quickActionDescriptors} />
      {/* Story 2-143b's own UI affordance — pin/unpin + reorder over the
          fixed widget catalog. Reads/writes the SAME query key this page's
          own `layoutData` renders from (`dashboardLayoutQueryKey` above), so
          a save is reflected immediately without a second round trip. */}
      <DashboardCustomizeControl layout={layoutData} queryKey={dashboardLayoutQueryKey} />
    </>
  );

  const viewMyDayAction = (testId: string): ActionDescriptor[] => [
    { id: 'view-my-day', label: 'View my day', onClick: () => navigate('/app/schedule'), 'data-testid': testId },
  ];

  const nextWorkBlock = nextWorkPinned ? (
    hasNextWork ? (
      // Repeated records, one line each — CompactRow (story 2-143a), not a
      // stack of full TaskCards. Same identity + status + open-action
      // grammar `ItemList.tsx` established for the Items collection,
      // generalised past `Inspection`. The accent bar TaskCard carried is
      // dropped (a decoration this dense a row has no room for); the state
      // it painted is still said in words via the due Badge, so nothing is
      // lost, only the redundant colour swatch.
      <Surface key="next-work" variant="flat" padding="xl">
        <Section
          title="Next up"
          description="Your own next work."
          actions={<HeaderActions actions={viewMyDayAction('dashboard-view-my-day')} />}
        >
          <CompactRowList data-testid="dashboard-next-work">
            {nextWork.map((item) => (
              <CompactRow
                key={item.inspectionId}
                data-testid={`dashboard-next-work-${item.inspectionId}`}
                title={item.siteName ?? 'Unknown site'}
                subtitle={item.typeName ?? 'No inspection type'}
                onOpen={() => navigate(workSurfaceFor(item.inspectionId))}
                openLabel={`Open ${item.siteName ?? 'inspection'}`}
                meta={
                  <>
                    <Badge tone={DUE_TONE[item.dueState]}>{DUE_LABEL[item.dueState]}</Badge>
                    {item.localTime && <span className="text-xs text-muted-foreground">{item.localTime}</span>}
                    {item.nextAction && (
                      <Badge tone={item.nextAction.allowed ? 'primary' : 'neutral'}>{item.nextAction.label}</Badge>
                    )}
                  </>
                }
                notice={item.nextAction && !item.nextAction.allowed ? <span>{item.nextAction.reason}</span> : undefined}
              />
            ))}
          </CompactRowList>
        </Section>
      </Surface>
    ) : (
      // Collapsed: a minimal single-line affordance, not a full-size card
      // holding only placeholder text. The heading stays a real h2 named
      // "Next up" either way — the same element story-2-105's own checks
      // look for by role/name.
      <Cluster
        key="next-work-empty"
        justify="between"
        gap={3}
        className="rounded-xl border border-border/60 px-4 py-3"
        data-testid="dashboard-next-work-empty"
      >
        <div>
          <h2 className="text-sm font-semibold text-foreground">Next up</h2>
          <p className="text-xs text-muted-foreground">Nothing on your plate right now.</p>
        </div>
        <HeaderActions actions={viewMyDayAction('dashboard-view-my-day')} />
      </Cluster>
    )
  ) : null;

  const reportsBlock = recentReportsPinned ? (
    hasApprovedReports ? (
      <Surface key="recent-reports" variant="flat" padding="xl">
        <Section title="Recently approved" description="Reports with a signed-off version.">
          <CompactRowList data-testid="dashboard-recent-reports">
            {recentApprovedReports.map((report) => (
              <CompactRow
                key={report.reportId}
                data-testid={`dashboard-report-${report.reportId}`}
                title={report.title ?? 'Untitled report'}
                {...(report.subjectName ? { subtitle: report.subjectName } : {})}
                onOpen={() => navigate(`/app/reports/${report.reportId}`)}
                openLabel={`Open ${report.title ?? 'report'}`}
                meta={
                  <>
                    <Badge tone="success">v{report.version}</Badge>
                    {report.approvedAt && (
                      <span className="text-xs text-muted-foreground">Approved {formatDistanceToNow(report.approvedAt)}</span>
                    )}
                  </>
                }
              />
            ))}
          </CompactRowList>
        </Section>
      </Surface>
    ) : (
      <Cluster
        key="recent-reports-empty"
        justify="between"
        gap={3}
        className="rounded-xl border border-border/60 px-4 py-3"
        data-testid="dashboard-reports-empty"
      >
        <div>
          <h2 className="text-sm font-semibold text-foreground">Recently approved</h2>
          <p className="text-xs text-muted-foreground">Nothing approved yet.</p>
        </div>
      </Cluster>
    )
  ) : null;

  return (
    <DashboardLayout
      header={{
        title: 'Dashboard',
        description: freshness,
        actions: quickActions,
        'data-testid': 'dashboard-header',
      }}
      data-testid="dashboard-page"
    >
      {isStale && (
        <Callout
          tone="warning"
          title="This dashboard may be out of date"
          data-testid="dashboard-stale"
          actions={
            <CardActions
              align="start"
              actions={[{ id: 'refresh-now', label: 'Refresh now', variant: 'secondary', onClick: () => refetch() }]}
            />
          }
        >
          {freshnessLabel} — the numbers below were correct as of then.
        </Callout>
      )}

      {zeroState.isEmpty ? (
        // WGTM step 1: a brand-new org gets a real, working setup action —
        // never an empty card with nothing to click.
        <>
          <div data-testid="dashboard-zero-state">
            <EmptyState
              headingLevel={2}
              illustration={<BuildingOffice2Icon className="h-8 w-8 translate-x-4 translate-y-4 text-primary" />}
              title={`Create your first ${subjectLower}`}
              description={`Nothing has been set up yet. Add a ${subjectLower} to start scheduling inspections and building reports.`}
              {...(newSiteGate.allowed
                ? { actionLabel: `New ${subjectLower}`, onAction: () => navigate('/app/sites') }
                : {})}
            />
          </div>
          {/* Story 2-106: the persisted first-value activation checklist (P16
              §6.1, WGTM step 1/11) — shown in the zero-state too (WGTM step 1
              pairs "Dashboard and activation checklist" as one sentence); it
              renders nothing on its own once dismissed/complete. */}
          <ActivationChecklistCard />
        </>
      ) : (
        <>
          {/* KPI strip → primary operational block ("Next up") → secondary
              progress block (the activation checklist) — the 3-part rhythm
              2026-08-22 §4.2 calls for. No change to story-2-105's real
              aggregate or story-2-106's checklist logic/testids below, only
              WHERE the checklist renders relative to the KPI strip. */}
          {/* KPI cards — real, permission-clamped counts. No delta/trend claim
              is invented; a count is shown as exactly what it is.
              Role-gating investigated for 2-143a and deliberately left
              ungated: `home.service.js#getHome` derives each count from
              `authzFor('<perm>:read')`, which zeroes a section this
              principal cannot read rather than 403ing the whole aggregate —
              so a genuine permission gap would already read as a (possibly
              misleading) 0, not a crash. But every SYSTEM_ROLE that can
              reach `/app` at all (owner/manager/inspector/reviewer/
              template-editor — `backend/src/config/systemRoles.js`) holds
              `subjects:read`, `inspections:read` AND `reports:read`; only
              the client-kind roles (`client-basic`/`client-approver`) lack
              them, and this app has no client-portal route under `/app` for
              those roles to land on this page from. So none of the four
              counts is currently misleading for any persona who can
              actually see this page — gating any of them would hide a real,
              visible number behind a permission check that never fires. */}
          {/* Story 2-143b: the three blocks below are rendered through
              `slotOrder` (derived from the user's own pinned-widget order)
              rather than in this fixed source order — see `slotOrderOf`
              above. Each block's OWN internal content/collapse logic
              (2-143a) is unchanged; only whether/where it appears is now
              driven by personalization. */}
          {slotOrder.map((slot) => {
            if (slot === 'kpiSummary') {
              return (
                <Grid key="kpiSummary" columns={{ base: 1, md: 4 }} gap={4}>
                  <KpiCard
                    title={subjectNounPlural}
                    value={counts.subjects.total.toString()}
                    icon={<BuildingOffice2Icon className="h-5 w-5" />}
                    data-testid="dashboard-kpi-subjects"
                  />
                  <KpiCard
                    title="Active inspections"
                    value={activeInspections.toString()}
                    icon={<CalendarDaysIcon className="h-5 w-5" />}
                    data-testid="dashboard-kpi-active"
                  />
                  <KpiCard
                    title="In review"
                    value={inReview.toString()}
                    icon={<ClipboardDocumentCheckIcon className="h-5 w-5" />}
                    data-testid="dashboard-kpi-in-review"
                  />
                  <KpiCard
                    title="Reports"
                    value={counts.reports.total.toString()}
                    icon={<DocumentTextIcon className="h-5 w-5" />}
                    data-testid="dashboard-kpi-reports"
                  />
                </Grid>
              );
            }

            if (slot === 'activationChecklist') {
              // Same real component, same testids/logic (story 2-106) —
              // `ActivationChecklistCard` still renders nothing on its own
              // once dismissed/complete, on top of now also needing this
              // widget to be pinned to be reached at all.
              return <ActivationChecklistCard key="activationChecklist" />;
            }

            // slot === 'workSection'
            if (!showWorkSection) return null;
            // Story 2-147wa: the 2:1 "Next up" / "Recently approved" pairing
            // is now `Split` (P10 §2.7 row 6) when BOTH sides have real data
            // to show side by side — the exact shape the old hand-rolled
            // `grid gap-6 lg:grid-cols-3` + `lg:col-span-2` pair implemented
            // by hand. Any other combination (one side collapsed/absent)
            // falls back to a plain vertical `Stack`, matching the old
            // fallback (`grid gap-6` with no column class — a single-column
            // stack) byte-for-byte.
            if (hasNextWork && hasApprovedReports) {
              return <Split key="workSection" primary={nextWorkBlock} secondary={reportsBlock} ratio="2:1" gap={6} />;
            }
            return (
              <Stack key="workSection" gap={6}>
                {nextWorkBlock}
                {reportsBlock}
              </Stack>
            );
          })}
        </>
      )}
    </DashboardLayout>
  );
}
